import React, { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { SOURCE_ORDER, sourceMeta } from '../../data/reservationSources';
import {
  comparisonSummary,
  partValueText,
  periodSummary,
  formatRange,
  notUsedNote,
  notUsedPanelNote,
  shiftsText,
  sourcesText,
  statusesText,
} from '../../lib/contextSummary';
import {
  coercePerfSettings,
  DEFAULT_COMPARE_KIND,
  DEFAULT_PERF_SETTINGS,
  effectiveCompareKind,
  frameCountBounds,
  FRAME_UNITS,
  frameUnitsFor,
  isDateRange,
} from '../../lib/perfSettings';
import {
  resolveComparisonWindow,
  resolvePeriodWindow,
  screenCapabilities,
  unusedActiveParts,
  type ContextPart,
  type ScreenId,
  type ViewContextState,
} from '../../lib/viewContext';
import type {
  FleetRestaurant,
  PerfCompareKind,
  PerfFrameUnit,
  PerfSettings,
  ReservationSourceId,
  ReservationStatusGroup,
  ScopeGroupConfig,
  ScopeGroupSummary,
} from '../../types';
import { RESERVATION_STATUS_GROUPS } from '../../types';
import { Icon } from '../shared/Icon';
import { DateRangePicker } from './DateRangePicker';
import { ContextOverlay, onListKeyDown } from './overlay';
import { VenueSection, type VenueCompareDraft } from './VenueSection';

export type ContextSection = ContextPart;

/** What the panel edits; applied in one go by its Apply. */
export type ContextDraft = ViewContextState & {
  compare: VenueCompareDraft;
};

const SECTIONS: ContextSection[] = ['venues', 'period', 'comparison', 'statuses', 'sources', 'shifts'];

const COMPARE_ORDER: PerfCompareKind[] = ['yoy_weekday', 'yoy', 'prior', 'custom', 'off'];

/** Statuses Growth never counts (decision 7.5.2). */
const GROWTH_NOT_OUTCOMES = new Set<ReservationStatusGroup>(['waitlist', 'rejected']);

const RANK: Record<string, number> = { day: 0, week: 1, month: 2, quarter: 3, year: 4 };

function sameList<T>(a: readonly T[], b: readonly T[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(b.map(x => String(x).toLowerCase()));
  return a.every(x => set.has(String(x).toLowerCase()));
}

function samePeriod(a: PerfSettings, b: PerfSettings): boolean {
  return (
    a.frameCount === b.frameCount &&
    a.frameUnit === b.frameUnit &&
    a.aggregation === b.aggregation &&
    (a.dateStart ?? null) === (b.dateStart ?? null) &&
    (a.dateEnd ?? null) === (b.dateEnd ?? null)
  );
}

function sameComparison(a: PerfSettings, b: PerfSettings): boolean {
  return (
    effectiveCompareKind(a) === effectiveCompareKind(b) &&
    Boolean(a.compareExplicit) === Boolean(b.compareExplicit) &&
    (a.compareDateStart ?? null) === (b.compareDateStart ?? null) &&
    (a.compareDateEnd ?? null) === (b.compareDateEnd ?? null)
  );
}

/** Which parts of the draft differ from what is on screen. */
export function changedParts(draft: ContextDraft, state: ViewContextState, compareNow: string[]): Set<ContextSection> {
  const out = new Set<ContextSection>();
  if (draft.regionFilter !== state.regionFilter) out.add('venues');
  if (draft.compare.supported && (draft.compare.on ? draft.compare.filters : []).join('|') !== compareNow.join('|')) {
    out.add('venues');
  }
  if (!samePeriod(draft.perfSettings, state.perfSettings)) out.add('period');
  if (!sameComparison(draft.perfSettings, state.perfSettings)) out.add('comparison');
  if (!sameList(draft.status, state.status)) out.add('statuses');
  if (!sameList(draft.source, state.source)) out.add('sources');
  if (!sameList(draft.shifts, state.shifts)) out.add('shifts');
  return out;
}

/** The parts the panel shows: the ones the screen uses, plus any set elsewhere it doesn't. */
export function panelSections(screen: ScreenId, state: ViewContextState): ContextSection[] {
  const uses = screenCapabilities(screen).uses;
  const unused = new Set(unusedActiveParts(screen, state));
  return SECTIONS.filter(part => uses[part] || unused.has(part) || (part === 'period' && screen === 'forecast'));
}

function periodWithUnit(settings: PerfSettings, unit: PerfFrameUnit): PerfSettings {
  let aggregation = settings.aggregation;
  // A unit finer than the chart's breakdown makes the breakdown that fine (Last 10 days → days).
  if (!frameUnitsFor(aggregation).includes(unit) && RANK[unit]! < RANK[aggregation]!) {
    aggregation = unit as PerfSettings['aggregation'];
  }
  return { ...settings, aggregation, frameUnit: unit };
}

export function ContextPanel({
  open,
  onClose,
  anchorRef,
  sheet,
  initialSection,
  screen,
  screenName,
  state,
  compareNow,
  onApply,
  restaurants,
  groups,
  groupConfigs,
  orgVenues,
  activeRestaurantId,
  restaurantId,
  onGroupsChanged,
  onManageGroups,
  availableShifts,
  compareSupported,
  describeVenues,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  sheet: boolean;
  initialSection: ContextSection;
  screen: ScreenId;
  screenName: string;
  state: ViewContextState;
  /** The venue filters compared side by side right now (two or more), else empty. */
  compareNow: string[];
  onApply: (draft: ContextDraft) => void;
  restaurants: FleetRestaurant[];
  groups: ScopeGroupSummary[];
  groupConfigs: ScopeGroupConfig[];
  orgVenues: { name: string | null; restaurantIds: string[] } | null;
  activeRestaurantId: string;
  restaurantId: string;
  onGroupsChanged?: () => void | Promise<void>;
  onManageGroups?: () => void;
  availableShifts: { name: string; outletCount: number; isEvent?: boolean }[];
  compareSupported: boolean;
  /** The venues a filter stands for, in the bar's words ("All venues of ButchersTable · 4 venues"). */
  describeVenues?: (filter: string) => string;
}) {
  const { t } = useTranslation('chrome');
  const titleId = 'cx-panel-title';
  const sections = useMemo(() => panelSections(screen, state), [screen, state]);
  const uses = screenCapabilities(screen).uses;
  const [section, setSection] = useState<ContextSection>(initialSection);
  const initial = (): ContextDraft => ({
    ...state,
    perfSettings: coercePerfSettings(state.perfSettings),
    compare: { supported: compareSupported, on: compareNow.length >= 2, filters: [...compareNow] },
  });
  const [draft, setDraft] = useState<ContextDraft>(initial);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Partial<Record<ContextSection, HTMLButtonElement | null>>>({});

  // The part to focus once its tab is on screen (after the dialog took focus).
  const focusOnMount = useRef<ContextSection | null>(null);

  // A fresh draft every time the panel opens, on the part that was clicked.
  useEffect(() => {
    if (!open) return;
    const first = sections.includes(initialSection) ? initialSection : (sections[0] ?? 'venues');
    setDraft(initial());
    setSection(first);
    focusOnMount.current = first;
  }, [open]);

  const changed = changedParts(draft, state, compareNow);
  const period = resolvePeriodWindow(draft.perfSettings);
  const customCompare = effectiveCompareKind(draft.perfSettings) === 'custom';
  const compareValid =
    !customCompare || Boolean(draft.perfSettings.compareDateStart && draft.perfSettings.compareDateEnd);
  const rangeValid =
    !isDateRange(draft.perfSettings) ||
    (draft.perfSettings.dateStart ?? '') <= (draft.perfSettings.dateEnd ?? '');
  const periodDraftValid =
    !draft.perfSettings.dateStart === !draft.perfSettings.dateEnd ? rangeValid : false;
  const venuesValid = draft.compare.on ? draft.compare.filters.length >= 2 : draft.regionFilter !== null;
  const valid = venuesValid && compareValid && periodDraftValid;

  const setPerf = (next: PerfSettings) => setDraft(d => ({ ...d, perfSettings: next }));

  const apply = () => {
    if (!valid || !changed.size) return;
    onApply({ ...draft, perfSettings: coercePerfSettings(draft.perfSettings) });
    onClose();
  };

  const reset = () => {
    const aggregation = draft.perfSettings.aggregation;
    setDraft(d => ({
      ...d,
      regionFilter: 'all',
      compare: { ...d.compare, on: false, filters: [] },
      perfSettings: coercePerfSettings({
        ...d.perfSettings,
        frameCount: DEFAULT_PERF_SETTINGS.frameCount,
        frameUnit: DEFAULT_PERF_SETTINGS.frameUnit,
        aggregation,
        dateStart: null,
        dateEnd: null,
        compareKind: undefined,
        compareExplicit: false,
        compareDateStart: null,
        compareDateEnd: null,
      }),
      status: [],
      source: [],
      shifts: [],
    }));
  };

  const clearPart = (part: ContextSection) => {
    if (part === 'statuses') setDraft(d => ({ ...d, status: [] }));
    if (part === 'sources') setDraft(d => ({ ...d, source: [] }));
    if (part === 'shifts') setDraft(d => ({ ...d, shifts: [] }));
    if (part === 'comparison')
      setPerf({ ...draft.perfSettings, compareKind: undefined, compareExplicit: false, compareDateStart: null, compareDateEnd: null });
    if (part === 'period')
      setPerf(
        coercePerfSettings({
          ...draft.perfSettings,
          frameCount: DEFAULT_PERF_SETTINGS.frameCount,
          frameUnit: DEFAULT_PERF_SETTINGS.frameUnit,
          dateStart: null,
          dateEnd: null,
        }),
      );
  };

  const onTabKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = sheet ? ['ArrowLeft', 'ArrowRight', 'Home', 'End'] : ['ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const at = sections.indexOf(section);
    let next = at;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (at + 1) % sections.length;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (at - 1 + sections.length) % sections.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = sections.length - 1;
    const part = sections[next]!;
    setSection(part);
    tabRefs.current[part]?.focus();
  };

  const draftSummary = (part: ContextSection): string => {
    if (part === 'venues') {
      if (draft.compare.on) return t('context.panel.venues.compareCount', { count: draft.compare.filters.length, max: 5 });
      return draft.regionFilter && describeVenues ? describeVenues(draft.regionFilter) : '';
    }
    return partValueText(part, {
      status: draft.status,
      source: draft.source,
      shifts: draft.shifts,
      period: periodSummary(period, draft.perfSettings),
      comparison: comparisonSummary(resolveComparisonWindow(draft.perfSettings, period), draft.perfSettings),
    });
  };

  const sectionTitle = (part: ContextSection) => t(`context.panel.${part}.title`);

  return (
    <ContextOverlay
      open={open}
      anchorRef={anchorRef}
      onClose={onClose}
      width={820}
      sheet={sheet}
      className="cx-panel"
      labelledBy={titleId}
    >
      <header className="cx-panel-head">
        <div>
          <h2 id={titleId}>{t('context.panel.title')}</h2>
          <p>{t('context.panel.subtitle', { screen: screenName })}</p>
        </div>
        <button type="button" className="cx-icon-btn" onClick={onClose} aria-label={t('context.panel.close')}>
          <Icon name="close" size={16} />
        </button>
      </header>
      <div className="cx-panel-main">
        <div
          className="cx-tabs"
          role="tablist"
          aria-orientation={sheet ? 'horizontal' : 'vertical'}
          aria-label={t('context.panel.sectionsAria')}
          onKeyDown={onTabKey}
        >
          {sections.map(part => {
            const selected = part === section;
            const notUsed = !uses[part];
            const summary = draftSummary(part);
            return (
              <button
                key={part}
                ref={el => {
                  tabRefs.current[part] = el;
                  if (el && focusOnMount.current === part) {
                    focusOnMount.current = null;
                    window.setTimeout(() => el.focus(), 0);
                  }
                }}
                type="button"
                role="tab"
                id={`cx-tab-${part}`}
                aria-selected={selected}
                aria-controls={`cx-tabpanel-${part}`}
                tabIndex={selected ? 0 : -1}
                className={`cx-tab${selected ? ' is-on' : ''}${notUsed ? ' is-unused' : ''}`}
                onClick={() => setSection(part)}
              >
                <span className="cx-tab-name">
                  {sectionTitle(part)}
                  {changed.has(part) ? (
                    <span className="cx-tab-dot" title={t('context.panel.changed')} aria-label={t('context.panel.changed')} />
                  ) : null}
                </span>
                {!sheet && summary ? <span className="cx-tab-sub">{summary}</span> : null}
                {notUsed ? <span className="cx-tab-unused">{notUsedNote(screen)}</span> : null}
              </button>
            );
          })}
        </div>
        <div
          ref={bodyRef}
          className="cx-section"
          role="tabpanel"
          id={`cx-tabpanel-${section}`}
          aria-labelledby={`cx-tab-${section}`}
        >
          <h3 className="cx-section-title">{sectionTitle(section)}</h3>
          {!uses[section] ? (
            <p className="cx-note is-muted" role="note">
              {notUsedPanelNote(screen)}{' '}
              {section !== 'venues' && unusedActiveParts(screen, draft).includes(section) ? (
                <button type="button" className="cx-link" onClick={() => clearPart(section)}>
                  {t('context.panel.clearPart')}
                </button>
              ) : null}
            </p>
          ) : null}

          {section === 'venues' ? (
            <VenueSection
              draft={draft.regionFilter}
              onDraft={filter => setDraft(d => ({ ...d, regionFilter: filter }))}
              restaurants={restaurants}
              groups={groups}
              groupConfigs={groupConfigs}
              orgVenues={orgVenues}
              activeRestaurantId={activeRestaurantId}
              restaurantId={restaurantId}
              onGroupsChanged={onGroupsChanged}
              onManageGroups={
                onManageGroups
                  ? () => {
                      onClose();
                      onManageGroups();
                    }
                  : undefined
              }
              compare={draft.compare}
              onCompare={next => setDraft(d => ({ ...d, compare: next }))}
              screenName={screenName}
            />
          ) : null}

          {section === 'period' ? (
            <PeriodSection settings={draft.perfSettings} onChange={setPerf} showBreakdown={screen.startsWith('performance')} />
          ) : null}

          {section === 'comparison' ? (
            <ComparisonSection settings={draft.perfSettings} onChange={setPerf} />
          ) : null}

          {section === 'statuses' ? (
            <div className="cx-checks">
              <div className="cx-presets" role="group" aria-label={t('context.panel.statuses.presets')}>
                <button
                  type="button"
                  className={`cx-preset${!draft.status.length ? ' is-on' : ''}`}
                  aria-pressed={!draft.status.length}
                  onClick={() => setDraft(d => ({ ...d, status: [] }))}
                >
                  <span>{t('context.panel.statuses.all')}</span>
                  <small>{t('context.panel.statuses.allHint')}</small>
                </button>
              </div>
              <Checklist
                legend={statusesText(draft.status)}
                items={RESERVATION_STATUS_GROUPS.map(g => ({
                  id: g.id,
                  label: g.label,
                  sub: g.tip,
                  note:
                    screen.startsWith('growth:') && GROWTH_NOT_OUTCOMES.has(g.id)
                      ? t('context.panel.statuses.notCounted')
                      : undefined,
                }))}
                selected={draft.status}
                onChange={next => setDraft(d => ({ ...d, status: next as ReservationStatusGroup[] }))}
              />
            </div>
          ) : null}

          {section === 'sources' ? (
            <div className="cx-checks">
              <AllButton
                on={!draft.source.length}
                label={t('context.panel.sources.all')}
                hint={t('context.panel.sources.allHint')}
                onClick={() => setDraft(d => ({ ...d, source: [] }))}
              />
              <Checklist
                legend={sourcesText(draft.source)}
                items={SOURCE_ORDER.map(id => {
                  const meta = sourceMeta(id);
                  return { id, label: meta.label, icon: meta.icon, color: meta.color };
                })}
                selected={draft.source}
                onChange={next => setDraft(d => ({ ...d, source: next as ReservationSourceId[] }))}
              />
            </div>
          ) : null}

          {section === 'shifts' ? (
            <div className="cx-checks">
              <AllButton
                on={!draft.shifts.length}
                label={t('context.panel.shifts.all')}
                hint={t('context.panel.shifts.allHint')}
                onClick={() => setDraft(d => ({ ...d, shifts: [] }))}
              />
              {availableShifts.length ? (
                <Checklist
                  legend={shiftsText(draft.shifts)}
                  items={availableShifts.map(row => ({
                    id: row.name,
                    label: row.name,
                    note: row.isEvent ? t('context.panel.shifts.event') : undefined,
                  }))}
                  selected={draft.shifts}
                  caseInsensitive
                  onChange={next => setDraft(d => ({ ...d, shifts: next }))}
                />
              ) : (
                <p className="cx-empty">{t('context.panel.shifts.none')}</p>
              )}
            </div>
          ) : null}
        </div>
      </div>
      <footer className="cx-panel-foot">
        <button type="button" className="cx-btn is-quiet" onClick={reset} title={t('context.panel.resetTitle')}>
          {t('context.panel.reset')}
        </button>
        <span className="cx-foot-spacer" />
        <button type="button" className="cx-btn is-primary" onClick={apply} disabled={!valid || !changed.size}>
          {t('context.panel.apply')}
        </button>
      </footer>
    </ContextOverlay>
  );
}

function AllButton({ on, label, hint, onClick }: { on: boolean; label: string; hint: string; onClick: () => void }) {
  return (
    <button type="button" className={`cx-preset is-all${on ? ' is-on' : ''}`} aria-pressed={on} onClick={onClick}>
      <span>{label}</span>
      <small>{hint}</small>
    </button>
  );
}

function Checklist({
  legend,
  items,
  selected,
  onChange,
  caseInsensitive = false,
}: {
  legend: string;
  items: { id: string; label: string; sub?: string; note?: string; icon?: string; color?: string }[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  caseInsensitive?: boolean;
}) {
  const norm = (s: string) => (caseInsensitive ? s.toLowerCase() : s);
  const set = new Set(selected.map(norm));
  return (
    <fieldset className="cx-group" onKeyDown={onListKeyDown}>
      <legend className="cx-sr">{legend}</legend>
      <div className="cx-list">
        {items.map(item => {
          const on = set.has(norm(item.id));
          return (
            <label key={item.id} className={`cx-opt${on ? ' is-on' : ''}`}>
              <input
                type="checkbox"
                data-cx-item
                checked={on}
                onChange={() =>
                  onChange(on ? selected.filter(x => norm(x) !== norm(item.id)) : [...selected, item.id])
                }
              />
              {item.icon ? (
                <Icon name={item.icon} size={13} style={{ color: item.color, flexShrink: 0 }} />
              ) : null}
              <span className="cx-opt-main">
                <span className="cx-opt-name">{item.label}</span>
                {item.sub ? <span className="cx-opt-sub">{item.sub}</span> : null}
              </span>
              {item.note ? <span className="cx-tag is-muted">{item.note}</span> : null}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function PeriodSection({
  settings,
  onChange,
  showBreakdown,
}: {
  settings: PerfSettings;
  onChange: (s: PerfSettings) => void;
  showBreakdown: boolean;
}) {
  const { t } = useTranslation('chrome');
  const custom = isDateRange(settings) || (settings.dateStart != null && settings.dateStart !== undefined);
  const window = resolvePeriodWindow(settings);
  const { min, max } = frameCountBounds(settings.aggregation, settings.frameUnit);
  const valid = !isDateRange(settings) || (settings.dateStart ?? '') <= (settings.dateEnd ?? '');
  return (
    <div className="cx-period">
      <div className="cx-seg" role="radiogroup" aria-label={t('context.panel.period.kindAria')}>
        <label className={`cx-seg-opt${!custom ? ' is-on' : ''}`}>
          <input
            type="radio"
            name="cx-period-kind"
            checked={!custom}
            onChange={() => onChange({ ...settings, dateStart: null, dateEnd: null })}
          />
          {t('context.panel.period.last')}
        </label>
        <label className={`cx-seg-opt${custom ? ' is-on' : ''}`}>
          <input
            type="radio"
            name="cx-period-kind"
            checked={custom}
            onChange={() => onChange({ ...settings, dateStart: window.start, dateEnd: window.end })}
          />
          {t('context.panel.period.custom')}
        </label>
      </div>
      {!custom ? (
        <div className="cx-frame">
          <label className="cx-field">
            <span>{t('context.panel.period.count')}</span>
            <input
              type="number"
              inputMode="numeric"
              min={min}
              max={max || undefined}
              value={settings.frameCount}
              onChange={e => {
                const n = Number.parseInt(e.target.value, 10);
                if (Number.isFinite(n)) onChange({ ...settings, frameCount: Math.max(1, n) });
              }}
            />
          </label>
          <label className="cx-field">
            <span>{t('context.panel.period.unit')}</span>
            <select
              value={settings.frameUnit}
              onChange={e => onChange(periodWithUnit(settings, e.target.value as PerfFrameUnit))}
            >
              {FRAME_UNITS.map(unit => (
                <option key={unit} value={unit}>
                  {t(settings.frameCount === 1 ? `units.${unit}One` : `units.${unit}Many`)}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <DateRangePicker
          idPrefix="cx-period"
          start={settings.dateStart ?? null}
          end={settings.dateEnd ?? null}
          onChange={(start, end) => onChange({ ...settings, dateStart: start, dateEnd: end })}
        />
      )}
      {valid ? (
        <p className="cx-resolved" aria-live="polite">
          <Icon name="calendar" size={13} />
          {t('context.panel.period.counts', { range: formatRange(window.start, window.end) })}
        </p>
      ) : (
        <p className="cx-note is-error" role="alert">
          {t('context.panel.period.invalid')}
        </p>
      )}
      {window.inProgress ? <p className="cx-note is-muted">{t('context.panel.period.inProgress')}</p> : null}
      {showBreakdown ? (
        <p className="cx-note is-muted">
          {t('context.panel.period.breakdown', { unit: t(`aggregation.${settings.aggregation}Plural`) })}
        </p>
      ) : null}
    </div>
  );
}

function ComparisonSection({ settings, onChange }: { settings: PerfSettings; onChange: (s: PerfSettings) => void }) {
  const { t } = useTranslation('chrome');
  const kind = effectiveCompareKind(settings);
  const period = resolvePeriodWindow(settings);
  const choose = (next: PerfCompareKind) => {
    if (next === DEFAULT_COMPARE_KIND) {
      onChange({ ...settings, compareKind: undefined, compareExplicit: false, compareDateStart: null, compareDateEnd: null });
      return;
    }
    if (next === 'custom') {
      const seed = resolveComparisonWindow({ ...settings, compareKind: 'yoy_weekday' }, period);
      onChange({
        ...settings,
        compareKind: 'custom',
        compareExplicit: true,
        compareDateStart: settings.compareDateStart ?? seed.start,
        compareDateEnd: settings.compareDateEnd ?? seed.end,
      });
      return;
    }
    onChange({ ...settings, compareKind: next, compareExplicit: true, compareDateStart: null, compareDateEnd: null });
  };
  return (
    <div className="cx-compare">
      <fieldset className="cx-group">
        <legend className="cx-sr">{t('context.panel.comparison.title')}</legend>
        <div className="cx-list">
          {COMPARE_ORDER.map(option => {
            const on = option === kind;
            const window =
              option === 'off' || option === 'custom'
                ? null
                : resolveComparisonWindow({ ...settings, compareKind: option, compareExplicit: true }, period);
            return (
              <label key={option} className={`cx-opt${on ? ' is-on' : ''}`}>
                <input type="radio" name="cx-compare-kind" checked={on} onChange={() => choose(option)} />
                <span className="cx-opt-main">
                  <span className="cx-opt-name">
                    {t(`context.panel.comparison.${option === 'off' ? 'none' : option}`)}
                    {option === DEFAULT_COMPARE_KIND ? (
                      <span className="cx-tag is-muted">{t('context.panel.comparison.default')}</span>
                    ) : null}
                  </span>
                  <span className="cx-opt-sub">
                    {t(`context.panel.comparison.${option === 'off' ? 'none' : option}Hint`)}
                    {window?.start && window.end ? ` · ${formatRange(window.start, window.end)}` : ''}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>
      {kind === 'custom' ? (
        <>
          <DateRangePicker
            idPrefix="cx-compare"
            start={settings.compareDateStart ?? null}
            end={settings.compareDateEnd ?? null}
            onChange={(start, end) => onChange({ ...settings, compareKind: 'custom', compareExplicit: true, compareDateStart: start, compareDateEnd: end })}
          />
          {!settings.compareDateStart || !settings.compareDateEnd ? (
            <p className="cx-note is-error" role="alert">
              {t('context.panel.comparison.pickDates')}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
